/**
 * The app-wide date range in the URL (PR 12, tracker C10).
 *
 * The store (lib/dateRangeContext.tsx) stays the truth; the URL is a carrier,
 * so a link, a bookmark or a bottom tab remembers the window it was opened
 * on. Pure helpers only - the component that wires them to the router is
 * components/AppDateRangeUrlSync.tsx.
 *
 *   ?from=2026-09-08&to=2026-10-07&preset=30d&cmp=previous
 *
 * `from`/`to` are always the concrete dates (a pasted link is explicit);
 * `preset` is there unless the dates were typed; a known preset is RE-DERIVED
 * when the link is opened, so a shared "Last 30 days" link rolls over daily
 * instead of freezing on the day it was copied.
 */
import { PRESETS, PRESET_ALIASES, rangeFromPreset, type DateRange, type DateRangePreset } from '../components/DateRangePicker'
import {
  customWindow, isValidYMD, parseCompareSetting,
  type CompareSetting,
} from './dateRangeCompare'
import type { AppDateRangeCore } from './dateRangeContext'

/** The query params this module owns; every other param is left alone. */
export const RANGE_PARAMS = ['from', 'to', 'preset', 'exclude', 'cmp'] as const

/** `cmp=` for a compare setting; null when there is nothing to carry. */
export function compareToParam(c: CompareSetting): string | null {
  if (c.mode === 'previous' || c.mode === 'last_year') return c.mode
  if (c.mode === 'custom' && isValidYMD(c.from) && isValidYMD(c.to)) return `custom:${c.from}..${c.to}`
  return null
}

/** The `compare=` a data request carries for the app's comparison (PR 12b, C4):
 *  'none' when no comparison is picked, the mode otherwise, `custom:a..b` for typed
 *  dates; undefined (omitted) for a custom comparison still missing its dates. */
export function compareParam(c: CompareSetting): string | undefined {
  if (c.mode === 'none') return 'none'
  return compareToParam(c) ?? undefined
}

/** The compare setting a `cmp=` value names; null for anything unreadable. */
function compareFromParam(value: string): CompareSetting | null {
  if (value === 'previous' || value === 'last_year') return { mode: value }
  if (value === 'none') return { mode: 'none' }
  const m = /^custom:(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/.exec(value)
  if (!m) return null
  const setting = parseCompareSetting({ mode: 'custom', from: m[1], to: m[2] })
  return setting.from && setting.to ? setting : null
}

/** The range (and comparison) as URL params. */
export function rangeToParams(core: Pick<AppDateRangeCore, 'range' | 'compare'>): URLSearchParams {
  const { range, compare } = core
  const p = new URLSearchParams()
  p.set('from', range.from)
  p.set('to', range.to)
  if (range.preset !== 'custom') p.set('preset', range.preset)
  const cmp = compareToParam(compare)
  if (cmp) p.set('cmp', cmp)
  return p
}

const KNOWN_PRESETS: ReadonlySet<string> = new Set([
  ...PRESETS.map(p => p.value as string), ...Object.keys(PRESET_ALIASES),
])

/**
 * The range a URL names, or null when it names none (no / unreadable
 * `from`+`to`). A known preset is re-derived at `now` (so the link rolls over);
 * anything else keeps the dates as written. `compare` is null when the URL
 * says nothing about it - the caller then leaves the stored comparison alone.
 */
export function rangeFromParams(
  params: URLSearchParams, now: Date = new Date(),
): { range: DateRange; compare: CompareSetting | null } | null {
  const from = params.get('from')
  const to = params.get('to')
  if (!isValidYMD(from) || !isValidYMD(to)) return null
  const rawPreset = params.get('preset')
  let range: DateRange
  if (rawPreset && rawPreset !== 'custom' && KNOWN_PRESETS.has(rawPreset)) {
    const preset = (PRESET_ALIASES[rawPreset as DateRangePreset] ?? rawPreset) as DateRangePreset
    range = rangeFromPreset(preset, 0, now)
  } else {
    const w = customWindow(from, to)
    if (!w) return null
    range = { from: w.from, to: w.to, preset: 'custom', lag: 0 }
  }
  const rawCmp = params.get('cmp')
  return { range, compare: rawCmp ? compareFromParam(rawCmp) : null }
}

/** `search` ("?a=1" or "a=1") with the range params replaced by `params` and
 *  every other param kept (Hourly `channel`, hub `tab`, ...). Returns a
 *  "?..." string, or '' when nothing is left. */
export function mergeRangeIntoSearch(search: string, params: URLSearchParams): string {
  const next = new URLSearchParams(search)
  for (const k of RANGE_PARAMS) next.delete(k)
  for (const [k, v] of params) next.set(k, v)
  const s = next.toString()
  return s ? `?${s}` : ''
}

/** True only for a location the sync did not write itself: a typed or pasted
 *  link, a link from another page. `key` is `${pathname}${search}`. A location
 *  the sync wrote (a bottom tab coming back to its remembered URL) is NOT
 *  adopted - the store already holds the latest pick, and one range serves the
 *  whole app. */
export function shouldAdoptFromUrl(key: string, written: ReadonlySet<string>): boolean {
  return !written.has(key)
}

/** Where the locations the sync wrote are remembered across reloads. */
export const WRITTEN_STORAGE_KEY = 'clickpay.dateRangeWritten.v1'

type WrittenStore = Pick<Storage, 'getItem' | 'setItem'>

/** The locations the sync wrote, from storage (best effort: empty when storage
 *  is missing, blocked or holds junk). Bottom tabs persist their URL, range
 *  params included, across a reload; without this memory the first click on a
 *  restored tab looks like a pasted link and silently replaces the app-wide
 *  range with that tab's old one. */
export function loadWritten(store: WrittenStore | null | undefined): Set<string> {
  try {
    const raw = store?.getItem(WRITTEN_STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [])
  } catch {
    return new Set()
  }
}

/** Remember `written` (best effort). */
export function saveWritten(store: WrittenStore | null | undefined, written: ReadonlySet<string>): void {
  try {
    store?.setItem(WRITTEN_STORAGE_KEY, JSON.stringify([...written]))
  } catch {
    /* private mode / quota: the set still works for this page load */
  }
}

/** Same window and settings (the dates a preset resolves to included). */
export function sameRange(a: DateRange, b: DateRange): boolean {
  return a.from === b.from && a.to === b.to && a.preset === b.preset
}
